import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, EyeOff, Eye, Check, Loader2, Flag, SearchX, Store, ChevronRight, Trash2, RotateCcw } from "lucide-react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { EmptyState } from "../components/dashboard/EmptyState";
import { AdminAccounts } from "../components/dashboard/AdminAccounts";
import { AdminInbox } from "../components/dashboard/AdminInbox";
import { AdminAccounting } from "../components/dashboard/AdminAccounting";
import { PasskeysCard } from "../components/dashboard/PasskeysCard";
import { TwoFactorCard } from "../components/dashboard/TwoFactorCard";
import { AdminDiscounts } from "../components/dashboard/AdminDiscounts";
import { AdminDealReview } from "../components/dashboard/AdminDealReview";
import { AdminVerifications } from "../components/dashboard/AdminVerifications";
import { AdminAppeals } from "../components/dashboard/AdminAppeals";
import { AdminTraffic } from "../components/dashboard/AdminTraffic";
import { AdminDemand } from "../components/dashboard/AdminDemand";
import { AdminAdKit } from "../components/dashboard/AdminAdKit";
import { AdminActivityLog } from "../components/dashboard/AdminActivityLog";
import { AdminStorage } from "../components/dashboard/AdminStorage";
import { AdminEmailUsage } from "../components/dashboard/AdminEmailUsage";
import { AdminFirewall } from "../components/dashboard/AdminFirewall";
import { AdminBackups } from "../components/dashboard/AdminBackups";
import { AdminCategories } from "../components/dashboard/AdminCategories";
import { AdminRates } from "../components/dashboard/AdminRates";
import { AdminBroadcast } from "../components/dashboard/AdminBroadcast";
import { AdminFeatures } from "../components/dashboard/AdminFeatures";
import { AdminMaintenance } from "../components/dashboard/AdminMaintenance";
import { AdminCachePurge } from "../components/dashboard/AdminCachePurge";
import { AdminPortalCard } from "../components/dashboard/AdminPortalCard";
import { AdminReviewDigest } from "../components/dashboard/AdminReviewDigest";
import { AdminPinHealth } from "../components/dashboard/AdminPinHealth";
import { AdminCrashes } from "../components/dashboard/AdminCrashes";
import { AdminCityCopy } from "../components/dashboard/AdminCityCopy";
import { AdminPowerUsers } from "../components/dashboard/AdminPowerUsers";
import { AdminGiftCredits } from "../components/dashboard/AdminGiftCredits";
import { AdminEmailTemplates } from "../components/dashboard/AdminEmailTemplates";
import { AdminLegal } from "../components/dashboard/AdminLegal";
import { AdminContent } from "../components/dashboard/AdminContent";
import { AdminRecycleBin } from "../components/dashboard/AdminRecycleBin";
import { AccountSecurityCard } from "../components/dashboard/AccountSecurityCard";
import { SessionsCard } from "../components/dashboard/SessionsCard";
import { MyMessagesCard } from "../components/dashboard/MyMessagesCard";

const Stat = ({ label, value, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className="group bg-card border border-border rounded-2xl p-4 text-center transition-all hover:border-[#16a34a] hover:shadow-md active:scale-[0.98]">
    <p className="text-2xl font-extrabold text-foreground">{value}</p>
    <p className="text-xs text-muted-foreground mt-0.5 group-hover:text-[#16a34a] transition-colors inline-flex items-center gap-0.5">{label} <ChevronRight size={12} /></p>
  </button>
);

const AdminDashboard = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("open");
  const [section, setSection] = useState(() => {
    const want = new URLSearchParams(window.location.search).get("tab");
    return ["money", "inbox", "moderation", "shops", "growth", "system", "account"].includes(want)
      ? want : "money";
  });
  const [busy, setBusy] = useState("");
  const [misses, setMisses] = useState([]);
  const [invites, setInvites] = useState([]);
  const navRef = useRef(null);

  const goSection = (id, t) => {
    setSection(id);
    if (t) setTab(t);
    setTimeout(() => navRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  useEffect(() => {
    api.get("/admin/search-misses").then((r) => setMisses(r.data)).catch(() => {});
    api.get("/admin/invites").then((r) => setInvites(r.data)).catch(() => {});
  }, []);

  const resetInvites = async () => {
    if (!window.confirm("Clear the whole 'Businesses shoppers want' list? This can't be undone.")) return;
    setBusy("reset-invites");
    try {
      const { data } = await api.delete("/admin/invites");
      setInvites([]);
      toast({ title: "List cleared", description: `${data.deleted} nomination${data.deleted === 1 ? "" : "s"} removed.` });
    } catch { toast({ title: "Could not clear that" }); }
    finally { setBusy(""); }
  };

  const resetMisses = async () => {
    if (!window.confirm("Clear the whole 'Zero-result searches' list? This can't be undone.")) return;
    setBusy("reset-misses");
    try {
      const { data } = await api.delete("/admin/search-misses");
      setMisses([]);
      toast({ title: "List cleared", description: `${data.deleted} search${data.deleted === 1 ? "" : "es"} removed.` });
    } catch { toast({ title: "Could not clear that" }); }
    finally { setBusy(""); }
  };

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([
        api.get(`/admin/reports?status=${tab}`),
        api.get("/admin/summary"),
      ]);
      setReports(r.data); setSummary(s.data);
    } catch { /* ignore */ }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "admin") {
    return <EmptyState icon={ShieldAlert} title="Admins only" message="This area is restricted to platform administrators." />;
  }

  const SECTIONS = [
    ["money", "Money"],
    ["inbox", "Inbox & Broadcast"],
    ["moderation", "Moderation"],
    ["shops", "Shops"],
    ["growth", "Growth"],
    ["system", "System"],
    ["account", "My account"],
  ];

  const resolve = async (id, action) => {
    setBusy(id + action);
    try {
      await api.post(`/admin/reports/${id}/resolve`, { action });
      toast({ title: action === "hide" ? "Deal hidden" : action === "unhide" ? "Deal restored" : "Report dismissed" });
      await load();
    } catch { toast({ title: "Action failed" }); }
    finally { setBusy(""); }
  };

  const clearAllReports = async () => {
    const scope = tab === "all" ? "all reports" : `all ${tab} reports`;
    if (!window.confirm(`Permanently delete ${scope} from the moderation queue?\n\nThis can't be undone. It does not affect the deals themselves.`)) return;
    setBusy("clear-reports");
    try {
      const { data } = await api.post(`/admin/reports/clear?status=${tab}`);
      toast({ title: "Reports cleared", description: `${data.deleted} report(s) removed.` });
      await load();
    } catch { toast({ title: "Could not clear reports" }); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-6 lp-fade-up" data-testid="admin-dashboard">
      <div className="relative overflow-hidden rounded-3xl bg-[#0f2a1a] text-white p-7 sm:p-9">
        <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-rose-500/30 blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-rose-200 backdrop-blur"><ShieldAlert size={13} /> Moderation</span>
          <h1 className="mt-3 text-3xl font-extrabold">Admin dashboard</h1>
          <p className="mt-2 text-sm text-emerald-100/80">Review reported deals and keep the marketplace clean.</p>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Open reports" value={summary.openReports} testid="stat-open-reports" onClick={() => goSection("moderation", "open")} />
          <Stat label="Hidden deals" value={summary.hiddenDeals} testid="stat-hidden-deals" onClick={() => goSection("moderation", "all")} />
          <Stat label="Total deals" value={summary.totalDeals} testid="stat-total-deals" onClick={() => goSection("moderation")} />
          <Stat label="Total users" value={summary.totalUsers} testid="stat-total-users" onClick={() => goSection("shops")} />
        </div>
      )}

      {/* One long scroll was unusable on a phone: everything lives behind a section now. */}
      <div ref={navRef} className="sticky top-0 z-30 -mx-4 px-4 py-2 bg-background/95 backdrop-blur border-b border-border sm:mx-0 sm:px-0 sm:border-0 sm:bg-transparent sm:backdrop-blur-none">
        <div className="flex gap-1 overflow-x-auto no-scrollbar bg-secondary rounded-full p-1">
          {SECTIONS.map(([id, label]) => (
            <button key={id} onClick={() => setSection(id)} data-testid={`admin-section-${id}`}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold transition-all ${section === id ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>
              {label}{id === "inbox" && summary?.newInbox > 0 ? ` (${summary.newInbox})` : ""}
              {id === "moderation" && summary?.openReports > 0 ? ` (${summary.openReports})` : ""}
            </button>
          ))}
        </div>
      </div>

      {section === "money" && <AdminAccounting />}
      {section === "money" && <AdminDiscounts />}
      {section === "inbox" && <AdminBroadcast />}
      {section === "inbox" && <AdminGiftCredits />}
      {section === "inbox" && <AdminInbox />}
      {section === "inbox" && <AdminAppeals />}
      {section === "shops" && <AdminVerifications />}
      {section === "shops" && <AdminAccounts />}
      {section === "growth" && <AdminTraffic />}
      {section === "growth" && <AdminAdKit />}
      {section === "growth" && <AdminDemand />}
      {section === "growth" && <AdminCityCopy />}
      {section === "growth" && <AdminPowerUsers />}
      {section === "system" && <AdminMaintenance />}
      {section === "system" && <AdminCachePurge />}
      {section === "system" && <AdminPortalCard />}
      {section === "system" && <AdminCrashes />}
      {section === "system" && <AdminFeatures />}
      {section === "system" && <AdminActivityLog />}
      {section === "system" && <AdminReviewDigest />}
      {section === "system" && <AdminPinHealth />}
      {section === "system" && <AdminEmailUsage />}
      {section === "system" && <AdminEmailTemplates />}
      {section === "system" && <AdminRates />}
      {section === "account" && <AccountSecurityCard />}
      {section === "account" && <SessionsCard />}
      {section === "account" && <TwoFactorCard />}
      {section === "account" && <PasskeysCard />}
      {section === "account" && <MyMessagesCard />}
      {section === "system" && <AdminContent />}
      {section === "system" && <AdminContent only="footer" />}
      {section === "system" && <AdminLegal />}
      {section === "system" && <AdminStorage />}
      {section === "system" && <AdminFirewall />}
      {section === "system" && <AdminRecycleBin />}
      {section === "system" && <AdminBackups />}

      {section === "moderation" && <AdminDealReview />}
      {section === "moderation" && <AdminCategories />}

      {section === "moderation" && (
      <>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex items-center gap-1 bg-secondary rounded-full p-1">
          {["open", "resolved", "all"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`px-4 py-1.5 rounded-full text-sm font-bold capitalize transition-all ${tab === t ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>{t}</button>
          ))}
        </div>
        {reports.length > 0 && (
          <button onClick={clearAllReports} disabled={busy === "clear-reports"} data-testid="clear-reports-btn"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-rose-600 border border-rose-500/40 hover:bg-rose-500/10 rounded-xl px-3 py-1.5 transition-colors disabled:opacity-50">
            {busy === "clear-reports" ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />} Clear all
          </button>
        )}
      </div>

      {reports.length === 0 ? (
        <EmptyState icon={Flag} title="Nothing to review" message="No reports in this view. Great — the marketplace looks clean!" />
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <div key={r.id} className="bg-card border border-border rounded-2xl p-4 flex flex-wrap items-center gap-4" data-testid={`report-row-${r.id}`}>
              {r.dealImage && <img src={r.dealImage} alt="" className="h-14 w-14 rounded-xl object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/deal/${r.dealId}`} className="font-bold text-foreground hover:text-[#16a34a] truncate">{r.dealTitle || "(deleted deal)"}</Link>
                  {r.dealHidden && <span className="text-[11px] font-bold bg-rose-100 text-rose-600 px-2 py-0.5 rounded-full">Hidden</span>}
                  {r.notHonoured && <span className="text-[11px] font-bold bg-rose-600 text-white px-2 py-0.5 rounded-full" data-testid={`not-honoured-${r.id}`}>Not honoured</span>}
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${r.status === "open" ? "bg-amber-100 text-amber-700" : "bg-secondary text-muted-foreground"}`}>{r.status}</span>
                </div>
                <p className="text-sm text-muted-foreground truncate">{r.business}</p>
                <p className="text-sm text-foreground mt-1"><span className="font-semibold">Reason:</span> {r.reason}</p>
              </div>
              <div className="flex items-center gap-2">
                {r.dealHidden ? (
                  <button onClick={() => resolve(r.id, "unhide")} disabled={busy === r.id + "unhide"} data-testid={`unhide-${r.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold border border-border hover:border-[#16a34a] rounded-xl px-3 py-2 transition-colors">{busy === r.id + "unhide" ? <Loader2 className="animate-spin" size={14} /> : <Eye size={14} />} Restore</button>
                ) : (
                  <button onClick={() => resolve(r.id, "hide")} disabled={busy === r.id + "hide"} data-testid={`hide-${r.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold bg-rose-500 hover:bg-rose-600 text-white rounded-xl px-3 py-2 transition-colors">{busy === r.id + "hide" ? <Loader2 className="animate-spin" size={14} /> : <EyeOff size={14} />} Hide deal</button>
                )}
                {r.status === "open" && (
                  <button onClick={() => resolve(r.id, "dismiss")} disabled={busy === r.id + "dismiss"} data-testid={`dismiss-${r.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold border border-border hover:bg-secondary rounded-xl px-3 py-2 transition-colors">{busy === r.id + "dismiss" ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Dismiss</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      </>
      )}

      {section === "growth" && (
      <div className="bg-card border border-border rounded-2xl p-5" data-testid="search-misses">
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-bold text-foreground flex items-center gap-2"><SearchX size={18} className="text-[#16a34a]" /> Zero-result searches</h2>
          {misses.length > 0 && (
            <button onClick={resetMisses} disabled={busy === "reset-misses"} data-testid="reset-misses-btn"
              className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-red-400 hover:text-red-500 text-muted-foreground rounded-xl px-3 py-1.5 transition-colors disabled:opacity-60">
              {busy === "reset-misses" ? <Loader2 className="animate-spin" size={13} /> : <RotateCcw size={13} />} Reset
            </button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 mb-3">What shoppers searched for and couldn't find — your merchant-recruitment shortlist.</p>
        {misses.length === 0 ? (
          <p className="text-sm text-muted-foreground">No missed searches yet 🎉</p>
        ) : (
          <div className="space-y-1.5">
            {misses.slice(0, 20).map((m) => (
              <div key={m.q} className="flex items-center gap-3 text-sm py-1.5 border-b border-border/60 last:border-0" data-testid={`search-miss-${m.q.replace(/[^a-z0-9]+/gi, "-")}`}>
                <span className="font-semibold text-foreground flex-1 min-w-0 truncate">“{m.q}”</span>
                <span className="text-xs text-muted-foreground shrink-0">{[m.city, m.country].filter(Boolean).join(", ") || "—"}</span>
                <span className="text-xs text-muted-foreground shrink-0">{m.lastAt ? String(m.lastAt).slice(0, 10) : ""}</span>
                <span className="text-xs font-bold text-white bg-[#16a34a] rounded-full px-2 py-0.5 shrink-0">{m.count}×</span>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {section === "growth" && (
      <div className="bg-card border border-border rounded-2xl p-5" data-testid="business-invites">
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-bold text-foreground flex items-center gap-2"><Store size={18} className="text-[#16a34a]" /> Businesses shoppers want</h2>
          {invites.length > 0 && (
            <button onClick={resetInvites} disabled={busy === "reset-invites"} data-testid="reset-invites-btn"
              className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-red-400 hover:text-red-500 text-muted-foreground rounded-xl px-3 py-1.5 transition-colors disabled:opacity-60">
              {busy === "reset-invites" ? <Loader2 className="animate-spin" size={13} /> : <RotateCcw size={13} />} Reset
            </button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 mb-3">Shops nominated by shoppers — the warmest leads you have.</p>
        {invites.length === 0 ? (
          <p className="text-sm text-muted-foreground">No nominations yet.</p>
        ) : (
          <div className="space-y-1.5">
            {invites.slice(0, 25).map((v) => (
              <div key={v.id} className="flex items-start gap-3 text-sm py-1.5 border-b border-border/60 last:border-0" data-testid={`business-invite-${v.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground truncate">{v.name}</p>
                  {v.note && <p className="text-xs text-muted-foreground truncate">“{v.note}”</p>}
                </div>
                <span className="text-xs text-muted-foreground shrink-0">{[v.city, v.country].filter(Boolean).join(", ") || "—"}</span>
                <span className="text-xs text-muted-foreground shrink-0 max-w-[160px] truncate">{v.email || "no email"}</span>
                <span className={`text-xs font-bold rounded-full px-2 py-0.5 shrink-0 ${v.emailed ? "bg-[#16a34a] text-white" : "bg-secondary text-muted-foreground"}`}>{v.emailed ? "emailed" : "logged"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default AdminDashboard;
